import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from './Toast';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface VmOption {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  privateIpAddress?: string;
  associatedNsgId?: string;
  associatedNsgName?: string;
}

interface EffectiveRule {
  priority: number;
  name: string;
  protocol: string;
  sourceAddressPrefix: string;
  destinationAddressPrefix: string;
  sourcePortRange: string;
  destinationPortRange: string;
  access: string;
  isDefault: boolean;
  sourceNsg: string;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
}

interface EffectiveSecurityRulesResult {
  vmId: string;
  vmName: string;
  nicName: string;
  privateIpAddress: string;
  associatedNsgId?: string;
  associatedNsgName: string;
  inboundRules: EffectiveRule[];
  outboundRules: EffectiveRule[];
}

interface HopDetail {
  hopNumber: number;
  address: string;
  type: string;
  status: string;
  latencyMs: number;
}

interface ConnectivityTestResult {
  sourceVm: string;
  sourceIp: string;
  destinationIp: string;
  destinationPort: number;
  protocol: string;
  isReachable: boolean;
  connectionState: string;
  blockingRuleName?: string;
  blockingNsg?: string;
  blockingDirection?: string;
  hops: HopDetail[];
  analysisSummary: string;
  latencyMs: number;
}

interface DnsResolutionResult {
  hostname: string;
  resolved: boolean;
  addresses: string[];
  cnameChain: string[];
  dnsServerUsed: string;
  recordType: string;
  responseTimeMs: number;
  error?: string;
}

interface EffectiveRoute {
  addressPrefix: string;
  nextHopType: string;
  nextHopIpAddress?: string;
  source: string;
  state: string;
}

interface RouteInspectorResult {
  vmId: string;
  vmName: string;
  nicName: string;
  subnetName: string;
  vnetName: string;
  routes: EffectiveRoute[];
}

type Tool = 'quickcheck' | 'rules' | 'connectivity' | 'dns' | 'routes';

// ── Helpers ───────────────────────────────────────────────────────────────────

const RISK_COLOR: Record<string, string> = {
  Low: '#6c757d',
  Medium: '#ffc107',
  High: '#fd7e14',
  Critical: '#dc3545',
};

const ACCESS_COLOR: Record<string, string> = {
  Allow: '#28a745',
  Deny: '#dc3545',
};

const HOP_TYPE_ICON: Record<string, string> = {
  Source: '💻',
  VirtualNetwork: '🌐',
  Gateway: '🔗',
  Internet: '🌍',
  Resource: '📦',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function RuleTable({ rules, title }: { rules: EffectiveRule[]; title: string }) {
  if (rules.length === 0) return null;
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h4 style={{ margin: '0 0 0.75rem', color: '#b0b0b0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title} ({rules.length})
      </h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.825rem' }}>
          <thead>
            <tr style={{ background: '#333338' }}>
              {['Priority', 'Name', 'Protocol', 'Source', 'Destination', 'Port', 'Access', 'Risk'].map(h => (
                <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 600, fontSize: '0.775rem' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #333', opacity: r.isDefault ? 0.65 : 1 }}>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', color: '#b0b0b0' }}>{r.priority}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  {r.name}
                  {r.isDefault && <span style={{ fontSize: '0.7rem', color: '#6c757d', marginLeft: '0.35rem' }}>(default)</span>}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace' }}>{r.protocol}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.775rem' }}>{r.sourceAddressPrefix}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.775rem' }}>{r.destinationAddressPrefix}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace' }}>{r.destinationPortRange}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <span style={{
                    padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 700, fontSize: '0.775rem',
                    background: ACCESS_COLOR[r.access] + '22', color: ACCESS_COLOR[r.access],
                    border: `1px solid ${ACCESS_COLOR[r.access]}55`
                  }}>
                    {r.access}
                  </span>
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <span style={{ color: RISK_COLOR[r.riskLevel], fontWeight: 600, fontSize: '0.775rem' }}>
                    {r.riskLevel}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const ConnectionTroubleshooter: React.FC = () => {
  const { showToast } = useToast();
  const [activeTool, setActiveTool] = useState<Tool>('rules');
  const [vms, setVms] = useState<VmOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [vmsLoading, setVmsLoading] = useState(true);

  // Effective Rules state
  const [rulesVmId, setRulesVmId] = useState('');
  const [rulesResult, setRulesResult] = useState<EffectiveSecurityRulesResult | null>(null);
  const [rulesTab, setRulesTab] = useState<'inbound' | 'outbound'>('inbound');

  // Connectivity state
  const [connSourceVmId, setConnSourceVmId] = useState('');
  const [connDestIp, setConnDestIp] = useState('');
  const [connDestPort, setConnDestPort] = useState('443');
  const [connProtocol, setConnProtocol] = useState('TCP');
  const [connResult, setConnResult] = useState<ConnectivityTestResult | null>(null);

  // DNS state
  const [dnsHostname, setDnsHostname] = useState('');
  const [dnsServer, setDnsServer] = useState('');
  const [dnsResult, setDnsResult] = useState<DnsResolutionResult | null>(null);

  // Routes state
  const [routesVmId, setRoutesVmId] = useState('');
  const [routesResult, setRoutesResult] = useState<RouteInspectorResult | null>(null);

  // Quick Check state
  const [qcVmId, setQcVmId] = useState('');
  const [qcDestHost, setQcDestHost] = useState('');
  const [qcDestPort, setQcDestPort] = useState('443');
  const [qcProtocol, setQcProtocol] = useState('TCP');
  const [qcRunning, setQcRunning] = useState(false);
  type QcStepStatus = 'pending' | 'running' | 'ok' | 'warn' | 'fail';
  interface QcStep { label: string; status: QcStepStatus; detail: string }
  const [qcSteps, setQcSteps] = useState<QcStep[]>([]);
  const [qcVerdict, setQcVerdict] = useState<{ ok: boolean; message: string } | null>(null);

  const loadVms = useCallback(async () => {
    setVmsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/connection/vms`, { params: { subscriptionId: 'all' } });
      setVms(res.data);
    } catch {
      showToast('Could not load VM list — some features may be unavailable.', 'warning');
    } finally {
      setVmsLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadVms(); }, [loadVms]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const fetchEffectiveRules = async () => {
    if (!rulesVmId) return;
    setLoading(true);
    setRulesResult(null);
    try {
      const res = await axios.post(`${API_URL}/connection/effective-rules`, { vmId: rulesVmId });
      setRulesResult(res.data);
    } catch (err: any) {
      showToast(extractErrorMessage(err, 'Failed to get effective rules.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const runConnectivityTest = async () => {
    if (!connSourceVmId || !connDestIp || !connDestPort) return;
    setLoading(true);
    setConnResult(null);
    try {
      const res = await axios.post(`${API_URL}/connection/test`, {
        sourceVmId: connSourceVmId,
        destinationIp: connDestIp,
        destinationPort: parseInt(connDestPort),
        protocol: connProtocol
      });
      setConnResult(res.data);
    } catch (err: any) {
      showToast(extractErrorMessage(err, 'Connectivity test failed.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const runDnsResolution = async () => {
    if (!dnsHostname) return;
    setLoading(true);
    setDnsResult(null);
    try {
      const res = await axios.post(`${API_URL}/connection/dns`, {
        hostname: dnsHostname,
        dnsServer: dnsServer || undefined
      });
      setDnsResult(res.data);
    } catch (err: any) {
      showToast(extractErrorMessage(err, 'DNS resolution failed.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const runRouteInspection = async () => {
    if (!routesVmId) return;
    setLoading(true);
    setRoutesResult(null);
    try {
      const res = await axios.post(`${API_URL}/connection/routes`, { vmId: routesVmId });
      setRoutesResult(res.data);
    } catch (err: any) {
      showToast(extractErrorMessage(err, 'Route inspection failed.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Quick Check handler ───────────────────────────────────────────────────

  const runQuickCheck = async () => {
    if (!qcVmId || !qcDestHost) return;
    setQcRunning(true);
    setQcVerdict(null);

    const step = (label: string): QcStep => ({ label, status: 'pending', detail: '' });
    const steps: QcStep[] = [
      step('NSG rules — check inbound/outbound rules on source VM'),
      step(`Connectivity — test ${qcDestHost}:${qcDestPort} (${qcProtocol})`),
      step('DNS — resolve destination hostname'),
    ];
    setQcSteps([...steps]);

    let overallOk = true;
    const problems: string[] = [];

    // Step 0: NSG rules
    steps[0] = { ...steps[0], status: 'running' };
    setQcSteps([...steps]);
    try {
      const res = await axios.post(`${API_URL}/connection/effective-rules`, { vmId: qcVmId });
      const rules: EffectiveRule[] = [...res.data.inboundRules, ...res.data.outboundRules];
      const portNum = parseInt(qcDestPort);
      const blocking = rules.filter(r =>
        r.access === 'Deny' &&
        (r.destinationPortRange === '*' ||
         r.destinationPortRange === qcDestPort ||
         (r.destinationPortRange.includes('-') && (() => {
           const [lo, hi] = r.destinationPortRange.split('-').map(Number);
           return portNum >= lo && portNum <= hi;
         })()))
      );
      if (blocking.length > 0) {
        steps[0] = { ...steps[0], status: 'warn', detail: `${blocking.length} Deny rule(s) may block port ${qcDestPort}: ${blocking.map(r => r.name).join(', ')}` };
        overallOk = false;
        problems.push(`NSG has ${blocking.length} Deny rule(s) that may block port ${qcDestPort}`);
      } else {
        steps[0] = { ...steps[0], status: 'ok', detail: `No blocking rules found for port ${qcDestPort}` };
      }
    } catch (e) {
      steps[0] = { ...steps[0], status: 'fail', detail: extractErrorMessage(e, 'Could not fetch NSG rules') };
      overallOk = false;
      problems.push('Could not retrieve NSG rules');
    }
    setQcSteps([...steps]);

    // Step 1: Connectivity test
    steps[1] = { ...steps[1], status: 'running' };
    setQcSteps([...steps]);
    try {
      const res = await axios.post(`${API_URL}/connection/test`, {
        sourceVmId: qcVmId,
        destinationIp: qcDestHost,
        destinationPort: parseInt(qcDestPort),
        protocol: qcProtocol,
      });
      const r: ConnectivityTestResult = res.data;
      if (r.isReachable) {
        steps[1] = { ...steps[1], status: 'ok', detail: `Reachable — latency ${r.latencyMs}ms (${r.connectionState})` };
      } else {
        steps[1] = { ...steps[1], status: 'warn', detail: `Status: ${r.connectionState}${r.blockingRuleName ? ` — blocked by rule "${r.blockingRuleName}"` : ''}` };
        overallOk = false;
        problems.push(`Connection status: ${r.connectionState}${r.blockingRuleName ? ` (rule: ${r.blockingRuleName})` : ''}`);
      }
    } catch (e) {
      steps[1] = { ...steps[1], status: 'fail', detail: extractErrorMessage(e, 'Connectivity test failed') };
      overallOk = false;
      problems.push('Connectivity test failed');
    }
    setQcSteps([...steps]);

    // Step 2: DNS (only if dest looks like a hostname, not an IP)
    const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(qcDestHost);
    if (!isIp) {
      steps[2] = { ...steps[2], status: 'running' };
      setQcSteps([...steps]);
      try {
        const res = await axios.post(`${API_URL}/connection/dns`, { hostname: qcDestHost });
        const r: DnsResolutionResult = res.data;
        if (r.resolved) {
          steps[2] = { ...steps[2], status: 'ok', detail: `Resolved to ${r.addresses.join(', ')}${r.cnameChain.length > 0 ? ` via CNAME: ${r.cnameChain.join(' → ')}` : ''}` };
        } else {
          steps[2] = { ...steps[2], status: 'warn', detail: `DNS could not resolve "${qcDestHost}"` };
          overallOk = false;
          problems.push(`DNS resolution failed for "${qcDestHost}"`);
        }
      } catch (e) {
        steps[2] = { ...steps[2], status: 'fail', detail: extractErrorMessage(e, 'DNS lookup failed') };
        overallOk = false;
        problems.push('DNS lookup failed');
      }
    } else {
      steps[2] = { ...steps[2], status: 'ok', detail: 'Skipped — destination is an IP address' };
    }
    setQcSteps([...steps]);

    setQcVerdict({
      ok: overallOk,
      message: overallOk
        ? `All checks passed. ${qcDestHost}:${qcDestPort} appears reachable from the selected VM.`
        : `Issues found: ${problems.join('; ')}.`,
    });
    setQcRunning(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const tools: { id: Tool; label: string; description: string }[] = [
    { id: 'quickcheck', label: '🚦 Quick Check', description: 'Guided wizard: pick a VM and destination, run all checks automatically and get a plain-English verdict.' },
    { id: 'rules', label: '🔐 Effective NSG Rules', description: 'See all merged NSG rules that apply to a VM — no more digging through blade after blade.' },
    { id: 'connectivity', label: '🔌 Port Connectivity', description: 'Test if a source VM can reach a destination IP:port. Identifies the blocking rule when traffic is denied.' },
    { id: 'dns', label: '🌐 DNS Resolution', description: 'Resolve a hostname through the VNet DNS chain and trace CNAME hops.' },
    { id: 'routes', label: '🗺️ Route Inspector', description: 'View the effective route table for a VM\'s NIC — user-defined, BGP, and default routes.' },
  ];

  const card: React.CSSProperties = {
    background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem'
  };

  const inputStyle: React.CSSProperties = {
    background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #444',
    borderRadius: '5px', padding: '0.5rem 0.75rem', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', marginBottom: '0.3rem', color: '#b0b0b0', fontSize: '0.825rem'
  };

  const btnPrimary: React.CSSProperties = {
    background: '#007bff', color: 'white', border: 'none', borderRadius: '6px',
    padding: '0.55rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
    opacity: loading ? 0.6 : 1
  };

  return (
    <div>
      {/* Tool selector */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {tools.map(t => (
          <div
            key={t.id}
            onClick={() => setActiveTool(t.id)}
            style={{
              ...card,
              cursor: 'pointer',
              borderColor: activeTool === t.id ? '#007bff' : '#444',
              background: activeTool === t.id ? '#007bff18' : '#25252a',
              transition: 'all 0.15s'
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>{t.label}</div>
            <div style={{ color: '#b0b0b0', fontSize: '0.8rem', lineHeight: 1.4 }}>{t.description}</div>
          </div>
        ))}
      </div>

      {/* ── Quick Check ──────────────────────────────────────────────────── */}
      {activeTool === 'quickcheck' && (
        <div style={{ ...card }}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Quick Connectivity Check</h3>
          <p style={{ margin: '0 0 1.25rem', color: '#888', fontSize: '0.875rem' }}>
            Select a source VM and destination, and we'll automatically run NSG rules, connectivity, and DNS checks — giving you a single verdict.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <label style={labelStyle}>Source VM *</label>
              <select value={qcVmId} onChange={e => setQcVmId(e.target.value)} style={inputStyle} disabled={vmsLoading}>
                <option value="">{vmsLoading ? 'Loading…' : 'Select VM…'}</option>
                {vms.map(v => <option key={v.id} value={v.id}>{v.name} ({v.privateIpAddress ?? 'no IP'})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Destination (IP or hostname) *</label>
              <input value={qcDestHost} onChange={e => setQcDestHost(e.target.value)} placeholder="e.g. 10.0.1.5 or api.example.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Port *</label>
              <input value={qcDestPort} onChange={e => setQcDestPort(e.target.value)} placeholder="443" style={{ ...inputStyle }} />
            </div>
            <div>
              <label style={labelStyle}>Protocol</label>
              <select value={qcProtocol} onChange={e => setQcProtocol(e.target.value)} style={inputStyle}>
                <option>TCP</option>
                <option>UDP</option>
              </select>
            </div>
          </div>

          <button
            style={{ ...btnPrimary, marginBottom: '1.5rem' }}
            disabled={!qcVmId || !qcDestHost || !qcDestPort || qcRunning}
            onClick={runQuickCheck}
          >
            {qcRunning ? '⟳ Running checks…' : '▶ Run Quick Check'}
          </button>

          {/* Steps */}
          {qcSteps.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
              {qcSteps.map((s, i) => {
                const icon = s.status === 'ok' ? '✅' : s.status === 'warn' ? '⚠️' : s.status === 'fail' ? '❌' : s.status === 'running' ? '⟳' : '○';
                const color = s.status === 'ok' ? '#28a745' : s.status === 'warn' ? '#ffc107' : s.status === 'fail' ? '#dc3545' : s.status === 'running' ? '#17a2b8' : '#666';
                return (
                  <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0.75rem', background: '#1a1a1e', borderRadius: 6, borderLeft: `3px solid ${color}` }}>
                    <span style={{ fontSize: '1rem', lineHeight: 1.3 }}>{icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: s.status === 'pending' ? '#666' : '#f0f0f0' }}>{s.label}</div>
                      {s.detail && <div style={{ fontSize: '0.8rem', color, marginTop: 2 }}>{s.detail}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Verdict */}
          {qcVerdict && (
            <div style={{
              padding: '1rem 1.25rem',
              borderRadius: 8,
              background: qcVerdict.ok ? '#28a74514' : '#dc354514',
              border: `1px solid ${qcVerdict.ok ? '#28a74555' : '#dc354555'}`,
              display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: '1.5rem' }}>{qcVerdict.ok ? '✅' : '❌'}</span>
              <div>
                <div style={{ fontWeight: 700, color: qcVerdict.ok ? '#28a745' : '#ff8f9a', marginBottom: 4 }}>
                  {qcVerdict.ok ? 'Connection looks good' : 'Issues detected'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#ccc' }}>{qcVerdict.message}</div>
                {!qcVerdict.ok && (
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#888' }}>
                    Use the individual tools above for detailed investigation.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Effective Rules ──────────────────────────────────────────────── */}
      {activeTool === 'rules' && (
        <div>
          <div style={{ ...card, marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 1rem' }}>Effective NSG Rules for a VM</h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '220px' }}>
                <label style={labelStyle}>Select VM</label>
                <select
                  value={rulesVmId}
                  onChange={e => setRulesVmId(e.target.value)}
                  style={inputStyle}
                  disabled={vmsLoading}
                >
                  <option value="">{vmsLoading ? 'Loading VMs…' : 'Select a VM…'}</option>
                  {vms.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.privateIpAddress ?? 'no IP'}) — {v.resourceGroup}
                    </option>
                  ))}
                </select>
              </div>
              <button
                style={btnPrimary}
                disabled={!rulesVmId || loading}
                onClick={fetchEffectiveRules}
              >
                {loading ? 'Fetching…' : 'Get Effective Rules'}
              </button>
            </div>
          </div>

          {rulesResult && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{rulesResult.vmName}</span>
                  <span style={{ color: '#b0b0b0', fontSize: '0.85rem', marginLeft: '0.75rem' }}>
                    NIC: {rulesResult.nicName}
                  </span>
                  <span style={{ color: '#b0b0b0', fontSize: '0.85rem', marginLeft: '0.75rem', fontFamily: 'monospace' }}>
                    {rulesResult.privateIpAddress}
                  </span>
                </div>
                <div style={{ marginLeft: 'auto', color: '#b0b0b0', fontSize: '0.875rem' }}>
                  NSG: <strong style={{ color: '#f0f0f0' }}>{rulesResult.associatedNsgName || 'None attached'}</strong>
                </div>
              </div>

              {/* Direction tabs */}
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid #444' }}>
                {(['inbound', 'outbound'] as const).map(dir => (
                  <button
                    key={dir}
                    onClick={() => setRulesTab(dir)}
                    style={{
                      background: 'none', border: 'none', borderBottom: rulesTab === dir ? '2px solid #007bff' : '2px solid transparent',
                      color: rulesTab === dir ? '#007bff' : '#b0b0b0',
                      padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                      marginBottom: '-1px', transition: 'all 0.15s', textTransform: 'capitalize'
                    }}
                  >
                    {dir === 'inbound' ? '↓ Inbound' : '↑ Outbound'} ({dir === 'inbound' ? rulesResult.inboundRules.length : rulesResult.outboundRules.length})
                  </button>
                ))}
              </div>

              {rulesTab === 'inbound' && (
                <RuleTable rules={rulesResult.inboundRules} title="Inbound rules (applied in priority order)" />
              )}
              {rulesTab === 'outbound' && (
                <RuleTable rules={rulesResult.outboundRules} title="Outbound rules (applied in priority order)" />
              )}

              {!rulesResult.associatedNsgId && (
                <div style={{ color: '#ffc107', padding: '1rem', textAlign: 'center' }}>
                  ⚠️ No NSG is attached to this VM's primary NIC. All traffic is governed by default VNet rules.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Connectivity Test ────────────────────────────────────────────── */}
      {activeTool === 'connectivity' && (
        <div>
          <div style={{ ...card, marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 1rem' }}>Port Connectivity Test</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Source VM</label>
                <select value={connSourceVmId} onChange={e => setConnSourceVmId(e.target.value)} style={inputStyle} disabled={vmsLoading}>
                  <option value="">{vmsLoading ? 'Loading…' : 'Select source VM…'}</option>
                  {vms.map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({v.privateIpAddress ?? '?'})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Destination IP</label>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="e.g. 10.2.0.4 or 8.8.8.8"
                  value={connDestIp}
                  onChange={e => setConnDestIp(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Destination Port</label>
                <input
                  style={inputStyle}
                  type="number"
                  min={1} max={65535}
                  placeholder="e.g. 443"
                  value={connDestPort}
                  onChange={e => setConnDestPort(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Protocol</label>
                <select value={connProtocol} onChange={e => setConnProtocol(e.target.value)} style={inputStyle}>
                  <option>TCP</option>
                  <option>UDP</option>
                  <option>ICMP</option>
                </select>
              </div>
            </div>
            <button
              style={btnPrimary}
              disabled={!connSourceVmId || !connDestIp || !connDestPort || loading}
              onClick={runConnectivityTest}
            >
              {loading ? 'Testing…' : 'Run Connectivity Test'}
            </button>
          </div>

          {connResult && (
            <div style={card}>
              {/* Result header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: connResult.isReachable ? '#28a74518' : '#dc354518',
                border: `1px solid ${connResult.isReachable ? '#28a74544' : '#dc354544'}`,
                borderRadius: '6px', marginBottom: '1.5rem'
              }}>
                <span style={{ fontSize: '2rem' }}>{connResult.isReachable ? '✅' : '❌'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem', color: connResult.isReachable ? '#28a745' : '#dc3545' }}>
                    {connResult.connectionState}
                  </div>
                  <div style={{ color: '#b0b0b0', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {connResult.sourceVm} ({connResult.sourceIp}) → {connResult.destinationIp}:{connResult.destinationPort} ({connResult.protocol})
                    {connResult.isReachable && <span style={{ marginLeft: '0.5rem' }}>· {connResult.latencyMs}ms</span>}
                  </div>
                </div>
                {connResult.blockingRuleName && (
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ color: '#dc3545', fontWeight: 600 }}>Blocked by</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{connResult.blockingRuleName}</div>
                    <div style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>
                      {connResult.blockingNsg} ({connResult.blockingDirection})
                    </div>
                  </div>
                )}
              </div>

              {/* Path hops */}
              <h4 style={{ margin: '0 0 0.75rem', color: '#b0b0b0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Network Path
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0', marginBottom: '1.5rem' }}>
                {connResult.hops.map((hop, i) => (
                  <React.Fragment key={hop.hopNumber}>
                    <div style={{
                      background: hop.status === 'Unreachable' ? '#dc354518' : '#333',
                      border: `1px solid ${hop.status === 'Unreachable' ? '#dc3545' : '#555'}`,
                      borderRadius: '8px', padding: '0.5rem 0.75rem', minWidth: '100px', textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.1rem' }}>{HOP_TYPE_ICON[hop.type] ?? '●'}</div>
                      <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', marginTop: '0.2rem' }}>{hop.address}</div>
                      <div style={{ fontSize: '0.7rem', color: '#b0b0b0' }}>{hop.type}</div>
                      {hop.latencyMs > 0 && (
                        <div style={{ fontSize: '0.7rem', color: '#17a2b8', marginTop: '0.15rem' }}>{hop.latencyMs}ms</div>
                      )}
                    </div>
                    {i < connResult.hops.length - 1 && (
                      <div style={{ padding: '0 0.25rem', color: '#555', fontSize: '1.2rem' }}>→</div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div style={{ color: '#b0b0b0', fontSize: '0.875rem', fontStyle: 'italic' }}>
                {connResult.analysisSummary}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DNS Resolution ────────────────────────────────────────────────── */}
      {activeTool === 'dns' && (
        <div>
          <div style={{ ...card, marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 1rem' }}>DNS Resolution</h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '2', minWidth: '220px' }}>
                <label style={labelStyle}>Hostname</label>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="e.g. prod-web-01.internal.contoso.com"
                  value={dnsHostname}
                  onChange={e => setDnsHostname(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && runDnsResolution()}
                />
              </div>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={labelStyle}>DNS Server (optional)</label>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="168.63.129.16 (Azure default)"
                  value={dnsServer}
                  onChange={e => setDnsServer(e.target.value)}
                />
              </div>
              <button
                style={btnPrimary}
                disabled={!dnsHostname || loading}
                onClick={runDnsResolution}
              >
                {loading ? 'Resolving…' : 'Resolve'}
              </button>
            </div>

            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {['prod-web-01.internal.contoso.com', 'api.contoso.com', 'prod-db.internal.contoso.com', 'login.microsoftonline.com', 'unknown.host.xyz'].map(h => (
                <button key={h} onClick={() => { setDnsHostname(h); }} style={{
                  background: '#333', border: '1px solid #555', color: '#b0b0b0',
                  borderRadius: '12px', padding: '0.2rem 0.65rem', cursor: 'pointer', fontSize: '0.775rem'
                }}>
                  {h}
                </button>
              ))}
            </div>
          </div>

          {dnsResult && (
            <div style={card}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: dnsResult.resolved ? '#28a74518' : '#dc354518',
                border: `1px solid ${dnsResult.resolved ? '#28a74544' : '#dc354544'}`,
                borderRadius: '6px', marginBottom: '1.25rem'
              }}>
                <span style={{ fontSize: '2rem' }}>{dnsResult.resolved ? '✅' : '❌'}</span>
                <div>
                  <div style={{ fontWeight: 700, color: dnsResult.resolved ? '#28a745' : '#dc3545' }}>
                    {dnsResult.resolved ? 'Resolved' : 'NXDOMAIN'}
                  </div>
                  <div style={{ fontFamily: 'monospace', color: '#b0b0b0', fontSize: '0.875rem' }}>
                    {dnsResult.hostname}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: '0.825rem', color: '#b0b0b0' }}>
                  <div>DNS: {dnsResult.dnsServerUsed}</div>
                  <div>{dnsResult.responseTimeMs}ms</div>
                </div>
              </div>

              {dnsResult.resolved && (
                <>
                  {dnsResult.cnameChain.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ color: '#b0b0b0', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>CNAME Chain</div>
                      {dnsResult.cnameChain.map((step, i) => (
                        <div key={i} style={{
                          padding: '0.35rem 0.75rem', background: '#333', borderRadius: '4px',
                          fontFamily: 'monospace', fontSize: '0.85rem', marginBottom: '0.25rem'
                        }}>
                          {step}
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <div style={{ color: '#b0b0b0', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                      {dnsResult.recordType} Records
                    </div>
                    {dnsResult.addresses.map((addr, i) => (
                      <div key={i} style={{
                        padding: '0.4rem 0.75rem', background: '#28a74522', border: '1px solid #28a74544',
                        borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9rem',
                        color: '#90ee90', marginBottom: '0.25rem'
                      }}>
                        {addr}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {dnsResult.error && (
                <div style={{ color: '#ff8f9a', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                  {dnsResult.error}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Route Inspector ──────────────────────────────────────────────── */}
      {activeTool === 'routes' && (
        <div>
          <div style={{ ...card, marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 1rem' }}>Effective Route Table</h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '220px' }}>
                <label style={labelStyle}>Select VM</label>
                <select value={routesVmId} onChange={e => setRoutesVmId(e.target.value)} style={inputStyle} disabled={vmsLoading}>
                  <option value="">{vmsLoading ? 'Loading VMs…' : 'Select a VM…'}</option>
                  {vms.map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({v.privateIpAddress ?? 'no IP'}) — {v.resourceGroup}</option>
                  ))}
                </select>
              </div>
              <button style={btnPrimary} disabled={!routesVmId || loading} onClick={runRouteInspection}>
                {loading ? 'Inspecting…' : 'Inspect Routes'}
              </button>
            </div>
          </div>

          {routesResult && (
            <div style={card}>
              <div style={{ marginBottom: '1rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                {[
                  ['VM', routesResult.vmName],
                  ['NIC', routesResult.nicName],
                  ['Subnet', routesResult.subnetName],
                  ['VNet', routesResult.vnetName],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ color: '#b0b0b0', fontSize: '0.775rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ fontWeight: 600, marginTop: '0.2rem' }}>{val}</div>
                  </div>
                ))}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#333338' }}>
                      {['Address Prefix', 'Next Hop Type', 'Next Hop IP', 'Source', 'State'].map(h => (
                        <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', color: '#b0b0b0', fontWeight: 600, fontSize: '0.775rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {routesResult.routes.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #333' }}>
                        <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace' }}>{r.addressPrefix}</td>
                        <td style={{ padding: '0.6rem 1rem' }}>
                          <span style={{
                            padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem',
                            background: '#33333855', border: '1px solid #555'
                          }}>
                            {r.nextHopType}
                          </span>
                        </td>
                        <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', color: r.nextHopIpAddress ? '#f0f0f0' : '#555' }}>
                          {r.nextHopIpAddress ?? '—'}
                        </td>
                        <td style={{ padding: '0.6rem 1rem' }}>
                          <span style={{
                            color: r.source === 'User' ? '#17a2b8' : r.source === 'BGP' ? '#ffc107' : '#6c757d',
                            fontWeight: r.source !== 'Default' ? 600 : 400
                          }}>
                            {r.source}
                          </span>
                        </td>
                        <td style={{ padding: '0.6rem 1rem' }}>
                          <span style={{ color: r.state === 'Active' ? '#28a745' : '#dc3545', fontWeight: 600 }}>
                            {r.state}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1.5rem', fontSize: '0.8rem', color: '#b0b0b0' }}>
                <span><strong style={{ color: '#17a2b8' }}>User</strong> — User-defined route table</span>
                <span><strong style={{ color: '#ffc107' }}>BGP</strong> — Learned from on-prem gateway</span>
                <span><strong style={{ color: '#6c757d' }}>Default</strong> — Azure system route</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionTroubleshooter;
